/**
 * Admin Ticket Departments Management Page
 * Manage support ticket departments and assignment rules
 */

import React, { useState, useEffect } from 'react';
import {
  FolderOpen,
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Mail,
  Users,
  Settings,
  Eye,
  EyeOff,
} from 'lucide-react';

interface Department {
  id: number;
  name: string;
  description: string;
  email: string;
  autoAssignStaffId?: number;
  autoAssignStaffName?: string;
  isPublic: boolean;
  ticketCount: number;
  openTicketCount: number;
  createdAt: string;
}

const TicketDepartmentsPage: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/admin/ticket-departments', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setDepartments(data.data.departments);
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDepartments = departments.filter((dept) => {
    const searchMatch =
      searchQuery === '' ||
      dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dept.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dept.email.toLowerCase().includes(searchQuery.toLowerCase());

    return searchMatch;
  });

  const handleDeleteDepartment = async (departmentId: number) => {
    if (
      !confirm(
        'Are you sure you want to delete this department? All tickets will need to be reassigned.'
      )
    )
      return;

    try {
      await fetch(`/api/v1/admin/ticket-departments/${departmentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadDepartments();
    } catch (error) {
      console.error('Failed to delete department:', error);
      alert('Failed to delete department');
    }
  };

  const totalTickets = departments.reduce((sum, d) => sum + d.ticketCount, 0);
  const totalOpenTickets = departments.reduce((sum, d) => sum + d.openTicketCount, 0);
  const publicDepartments = departments.filter((d) => d.isPublic).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FolderOpen className="w-8 h-8" />
            Ticket Departments
          </h1>
          <p className="text-slate-400 mt-2">
            Manage support departments and ticket routing
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 font-medium"
        >
          <Plus className="w-5 h-5" />
          New Department
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Departments</p>
              <p className="text-2xl font-bold text-white mt-1">{departments.length}</p>
            </div>
            <FolderOpen className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Public Departments</p>
              <p className="text-2xl font-bold text-green-400 mt-1">{publicDepartments}</p>
            </div>
            <Eye className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Tickets</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">{totalTickets}</p>
            </div>
            <Mail className="w-10 h-10 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Open Tickets</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{totalOpenTickets}</p>
            </div>
            <RefreshCw className="w-10 h-10 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search departments by name, description, or email..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>
      </div>

      {/* Departments List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading departments...</span>
          </div>
        </div>
      ) : filteredDepartments.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Departments Found</h3>
          <p className="text-slate-400">
            {searchQuery
              ? 'No departments match your search.'
              : 'Create your first support department to get started.'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create Department
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDepartments.map((department) => (
            <div
              key={department.id}
              className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 hover:border-orange-500 transition-colors"
            >
              {/* Department Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-white">{department.name}</h3>
                    {department.isPublic ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/20 text-green-400">
                        <Eye className="w-3 h-3 mr-1" />
                        Public
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-900/20 text-slate-400">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Private
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 line-clamp-2">
                    {department.description}
                  </p>
                </div>
              </div>

              {/* Department Email */}
              <div className="mb-4 pb-4 border-b border-slate-700/50">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300">{department.email}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-slate-400">Total Tickets</p>
                  <p className="text-lg font-semibold text-white">{department.ticketCount}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Open Tickets</p>
                  <p className="text-lg font-semibold text-yellow-400">
                    {department.openTicketCount}
                  </p>
                </div>
              </div>

              {/* Auto-assign Info */}
              {department.autoAssignStaffName && (
                <div className="mb-4 pb-4 border-b border-slate-700/50">
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400">Auto-assign to:</span>
                    <span className="text-white font-medium">
                      {department.autoAssignStaffName}
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingDepartment(department)}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteDepartment(department.id)}
                  className="px-4 py-2 bg-slate-700/50 text-red-400 rounded-lg hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                  title="Delete Department"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal Placeholder */}
      {(showCreateModal || editingDepartment) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold text-white mb-4">
              {editingDepartment ? 'Edit Department' : 'Create Department'}
            </h2>
            <p className="text-slate-400 mb-6">
              Department form will be implemented here with fields for name, description,
              email, visibility, and auto-assignment settings.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingDepartment(null);
                }}
                className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-colors"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                {editingDepartment ? 'Save Changes' : 'Create Department'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDepartmentsPage;
